import type { AisleGraph } from "../types";

export type CadPoint = { x: number; y: number };

type WalkNode = {
  id: string;
  x: number;
  y: number;
  kind: string;
  boothId: string | null;
};

type WalkNeighbor = { to: string; weight: number };

export type WalkGraph = {
  nodes: WalkNode[];
  byId: Map<string, WalkNode>;
  adj: Map<string, WalkNeighbor[]>;
};

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function str(value: unknown): string {
  return typeof value === "string" ? value : value == null ? "" : String(value);
}

function num(value: unknown): number | null {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function pointXY(geometry: unknown): CadPoint | null {
  const geom = asRecord(geometry);
  if (geom.type !== "Point" || !Array.isArray(geom.coordinates)) return null;
  const x = num(geom.coordinates[0]);
  const y = num(geom.coordinates[1]);
  if (x == null || y == null) return null;
  return { x, y };
}

function lineEndpoints(geometry: unknown): { a: CadPoint; b: CadPoint } | null {
  const geom = asRecord(geometry);
  if (geom.type !== "LineString" || !Array.isArray(geom.coordinates) || geom.coordinates.length < 2) return null;
  const first = geom.coordinates[0];
  const last = geom.coordinates[geom.coordinates.length - 1];
  if (!Array.isArray(first) || !Array.isArray(last)) return null;
  const x1 = num(first[0]);
  const y1 = num(first[1]);
  const x2 = num(last[0]);
  const y2 = num(last[1]);
  if (x1 == null || y1 == null || x2 == null || y2 == null) return null;
  return { a: { x: x1, y: y1 }, b: { x: x2, y: y2 } };
}

export function buildWalkGraph(graph: AisleGraph | null | undefined): WalkGraph | null {
  if (!graph?.nodes?.features?.length || !graph.edges?.features?.length) return null;
  const nodes: WalkNode[] = [];
  const byId = new Map<string, WalkNode>();
  for (const feature of graph.nodes.features) {
    const props = asRecord(feature.properties);
    const id = str(props.node_id);
    const xy = pointXY(feature.geometry);
    if (!id || !xy) continue;
    const node: WalkNode = {
      id,
      x: xy.x,
      y: xy.y,
      kind: str(props.kind) || "junction",
      boothId: str(props.booth_id) || null,
    };
    nodes.push(node);
    byId.set(id, node);
  }
  const adj = new Map<string, WalkNeighbor[]>();
  const link = (from: string, to: string, weight: number) => {
    const list = adj.get(from);
    if (list) list.push({ to, weight });
    else adj.set(from, [{ to, weight }]);
  };
  for (const feature of graph.edges.features) {
    const props = asRecord(feature.properties);
    const from = str(props.from);
    const to = str(props.to);
    if (!from || !to || from === to || !byId.has(from) || !byId.has(to)) continue;
    const ends = lineEndpoints(feature.geometry);
    const weight = num(props.weight) ?? (ends ? Math.hypot(ends.b.x - ends.a.x, ends.b.y - ends.a.y) : 1);
    link(from, to, Math.max(weight, 1));
    link(to, from, Math.max(weight, 1));
  }
  return { nodes, byId, adj };
}

export function findBoothNodeId(walk: WalkGraph, booth: string): string | null {
  const want = booth.trim().toLowerCase();
  if (!want) return null;
  const boothNode = walk.nodes.find((node) => node.kind === "booth" && node.boothId?.toLowerCase() === want);
  if (boothNode) return boothNode.id;
  const any = walk.nodes.find((node) => node.boothId?.toLowerCase() === want);
  return any?.id ?? null;
}

export function nearestNodeId(walk: WalkGraph, x: number, y: number): string | null {
  let best: string | null = null;
  let bestD = Infinity;
  for (const node of walk.nodes) {
    const d = Math.hypot(node.x - x, node.y - y);
    if (d < bestD) {
      bestD = d;
      best = node.id;
    }
  }
  return best;
}

export function shortestWalk(walk: WalkGraph, startId: string, goalId: string): CadPoint[] | null {
  if (startId === goalId) {
    const node = walk.byId.get(startId);
    return node ? [{ x: node.x, y: node.y }] : null;
  }
  const start = walk.byId.get(startId);
  const goal = walk.byId.get(goalId);
  if (!start || !goal) return null;

  const h = (id: string) => {
    const node = walk.byId.get(id);
    return node ? Math.hypot(node.x - goal.x, node.y - goal.y) : 0;
  };

  const open = new Set<string>([startId]);
  const came = new Map<string, string>();
  const gScore = new Map<string, number>([[startId, 0]]);
  const fScore = new Map<string, number>([[startId, h(startId)]]);

  while (open.size) {
    let current: string | undefined;
    let best = Infinity;
    for (const id of open) {
      const score = fScore.get(id) ?? Infinity;
      if (score < best) {
        best = score;
        current = id;
      }
    }
    if (!current) break;
    if (current === goalId) {
      const path = [current];
      let walkId = current;
      while (came.has(walkId)) {
        walkId = came.get(walkId)!;
        path.push(walkId);
      }
      path.reverse();
      return path.map((id) => {
        const node = walk.byId.get(id)!;
        return { x: node.x, y: node.y };
      });
    }
    open.delete(current);
    const currentG = gScore.get(current) ?? Infinity;
    for (const nb of walk.adj.get(current) ?? []) {
      const tentative = currentG + nb.weight;
      if (tentative < (gScore.get(nb.to) ?? Infinity)) {
        came.set(nb.to, current);
        gScore.set(nb.to, tentative);
        fScore.set(nb.to, tentative + h(nb.to));
        open.add(nb.to);
      }
    }
  }
  return null;
}

export function routeBoothToBooth(
  graph: AisleGraph | null | undefined,
  fromBooth: string,
  toBooth: string,
): CadPoint[] | null {
  const walk = buildWalkGraph(graph);
  if (!walk) return null;
  const start = findBoothNodeId(walk, fromBooth);
  const goal = findBoothNodeId(walk, toBooth);
  if (!start || !goal) return null;
  return shortestWalk(walk, start, goal);
}

export function routeFromPointToBooth(
  graph: AisleGraph | null | undefined,
  origin: CadPoint,
  toBooth: string,
): CadPoint[] | null {
  const walk = buildWalkGraph(graph);
  if (!walk) return null;
  const start = nearestNodeId(walk, origin.x, origin.y);
  const goal = findBoothNodeId(walk, toBooth);
  if (!start || !goal) return null;
  return shortestWalk(walk, start, goal);
}

/** Leaflet CRS.Simple uses [y, x] as LatLng. */
export function cadPathToLatLngs(path: CadPoint[]): Array<[number, number]> {
  return path.map((point) => [point.y, point.x]);
}
